import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardBody, CardHeader, Field, Input, StatusPill } from '~/components/ui';
import { EntityPicker } from '~/components/migrate/EntityPicker';
import { ColumnMapper } from '~/components/migrate/ColumnMapper';
import { PreviewTable } from '~/components/migrate/PreviewTable';
import { ImportHistoryList } from '~/components/migrate/ImportHistoryList';
import { parseCsv, readFileAsText, type ParsedCsv } from '~/lib/csv/parse';
import { autoMap, listMissingRequired, transformRow, type ColumnMapping } from '~/lib/csv/transform';
import { getEntityDef } from '~/lib/csv/import-config';
import { postImport } from '~/lib/api/migrate';
import { supabase } from '~/lib/supabase';

export const Route = createFileRoute('/_app/admin/migrate')({
  component: MigrateConsole,
});

function MigrateConsole() {
  const qc = useQueryClient();
  const [entity, setEntity] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [csv, setCsv] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [running, setRunning] = useState<'idle' | 'dry' | 'live'>('idle');
  const [lastResult, setLastResult] = useState<{ ok: boolean; successCount?: number; errorCount?: number; errors?: Array<{ row: number; error: string }>; importId?: string; status?: string; error?: string; dryRun?: boolean } | null>(null);

  const entityDef = entity ? getEntityDef(entity) : null;

  // Pull import history to determine which entities are "completed"
  const { data: completed } = useQuery({
    queryKey: ['migration_imports', 'completed'],
    queryFn: async () => {
      const { data } = await supabase
        .from('migration_imports')
        .select('entity_type, status, dry_run, success_count')
        .eq('status', 'completed')
        .eq('dry_run', false)
        .gt('success_count', 0);
      return new Set((data ?? []).map((d) => d.entity_type));
    },
  });

  // Load default preset mapping for the chosen entity
  const { data: presetMap } = useQuery({
    queryKey: ['migration_column_presets', entity],
    enabled: !!entity,
    queryFn: async () => {
      const { data } = await supabase
        .from('migration_column_presets')
        .select('mapping')
        .eq('entity_type', entity!)
        .eq('is_default', true)
        .maybeSingle();
      return (data?.mapping as Record<string, string>) ?? null;
    },
  });

  // Auto-map when CSV + preset are both ready
  useEffect(() => {
    if (csv && entityDef) setMapping(autoMap(csv.headers, entityDef.entity, presetMap ?? undefined));
  }, [csv, entityDef, presetMap]);

  const transformed = useMemo(() => {
    if (!csv || !entityDef) return [];
    return csv.rows.slice(0, 5).map((r) => transformRow(r, mapping, entityDef));
  }, [csv, entityDef, mapping]);

  const allTransformed = useMemo(() => {
    if (!csv || !entityDef) return [];
    return csv.rows.map((r) => transformRow(r, mapping, entityDef));
  }, [csv, entityDef, mapping]);

  const missingRequired = entityDef ? listMissingRequired(entityDef, mapping) : [];
  const validationErrors = allTransformed.filter((t) => t.errors.length > 0).length;

  async function handleFile(f: File) {
    setFile(f);
    const text = await readFileAsText(f);
    const parsed = parseCsv(text);
    setCsv(parsed);
    setLastResult(null);
  }

  async function runImport(dryRun: boolean) {
    if (!entityDef || !csv || !file) return;
    setRunning(dryRun ? 'dry' : 'live');
    setLastResult(null);
    try {
      const rows = allTransformed.filter((t) => t.errors.length === 0).map((t) => t.payload);
      if (rows.length === 0) {
        setLastResult({ ok: false, error: 'No valid rows to import. Fix column mapping or input data.' });
        return;
      }
      const map: Record<string, string> = {};
      for (const [k, v] of Object.entries(mapping)) if (v) map[k] = v;
      const res = await postImport({
        entity: entityDef.entity,
        filename: file.name,
        mapping: map,
        rows,
        dryRun,
      });
      setLastResult(res);
      qc.invalidateQueries({ queryKey: ['migration_imports'] });
    } finally {
      setRunning('idle');
    }
  }

  return (
    <div className="px-6 py-6 space-y-6 max-w-7xl mx-auto">
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--color-navy)]">Migration Console</h1>
        <p className="text-sm text-[var(--color-ink-soft)] mt-1">
          Upload Dash CSV exports. Always run a dry run before the real import. The order matters:
          customers → jobs → claims/dates/notes/etc.
        </p>
      </div>

      <div className="rounded-lg border border-[var(--color-blue-deep)] bg-[var(--color-blue-tint)] px-4 py-3 text-sm text-[var(--color-blue-deep)]">
        <strong>Cutover / edge-case tool.</strong> Day-to-day, the CRM mirrors Dash automatically
        via the <code className="font-mono">dash-sync</code> worker (hourly, same SharePoint export,
        idempotent by external&nbsp;ref) — no manual import needed. Use this console only for the
        initial load, a one-off backfill, or non-Dash data. See <code className="font-mono">DASH-SYNC-GUIDE.md</code>.
      </div>

      <Card>
        <CardHeader>1. Pick what you're importing</CardHeader>
        <CardBody>
          <EntityPicker
            value={entity}
            onChange={(e) => { setEntity(e); setCsv(null); setFile(null); setLastResult(null); }}
            completedEntities={completed ?? new Set()}
          />
        </CardBody>
      </Card>

      {entityDef && (
        <Card>
          <CardHeader>
            2. Upload {entityDef.label} CSV
          </CardHeader>
          <CardBody className="space-y-4">
            {entityDef.uploadHint && (
              <div className="text-xs px-3 py-2 rounded bg-[var(--color-blue-tint)] text-[var(--color-blue-deep)]">
                {entityDef.uploadHint}
              </div>
            )}
            {entityDef.dependsOn.length > 0 && (
              <div className="text-xs px-3 py-2 rounded bg-[var(--color-warn-tint)] text-[var(--color-warn)]">
                Reminder: import {entityDef.dependsOn.join(', ')} first.
              </div>
            )}
            <Field label="CSV file" htmlFor="file">
              <Input id="file" type="file" accept=".csv,text/csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }} />
            </Field>
            {file && csv && (
              <div className="text-sm text-[var(--color-ink-soft)]">
                <span className="font-semibold">{file.name}</span> · {csv.rows.length.toLocaleString()} rows · {csv.headers.length} columns
                {csv.errors.length > 0 && (
                  <span className="text-[var(--color-warn)] ml-2">({csv.errors.length} parse warnings)</span>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {entityDef && csv && (
        <Card>
          <CardHeader action={
            missingRequired.length > 0
              ? <StatusPill kind="overdue">{missingRequired.length} required field(s) unmapped</StatusPill>
              : <StatusPill kind="completed">All required fields mapped</StatusPill>
          }>
            3. Map columns
          </CardHeader>
          <CardBody>
            <ColumnMapper entity={entityDef} headers={csv.headers} mapping={mapping} onChange={setMapping} />
          </CardBody>
        </Card>
      )}

      {entityDef && csv && (
        <Card>
          <CardHeader action={
            validationErrors === 0
              ? <StatusPill kind="completed">{csv.rows.length} valid</StatusPill>
              : <StatusPill kind="warning">{validationErrors} invalid / {csv.rows.length} total</StatusPill>
          }>
            4. Preview (first 5 rows)
          </CardHeader>
          <CardBody>
            <PreviewTable rows={transformed} />
          </CardBody>
        </Card>
      )}

      {entityDef && csv && (
        <Card>
          <CardHeader>5. Import</CardHeader>
          <CardBody className="space-y-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" disabled={running !== 'idle' || missingRequired.length > 0}
                onClick={() => runImport(true)}>
                {running === 'dry' ? 'Running dry run…' : 'Dry run'}
              </Button>
              <Button variant="primary" disabled={running !== 'idle' || missingRequired.length > 0}
                onClick={() => {
                  if (confirm(`Import ${csv.rows.length - validationErrors} ${entityDef.label} rows for real?`)) runImport(false);
                }}>
                {running === 'live' ? 'Importing…' : 'Import for real'}
              </Button>
              {missingRequired.length > 0 && (
                <span className="text-xs text-[var(--color-danger)]">
                  Map all required fields first.
                </span>
              )}
            </div>
            {lastResult && (
              <div className={
                'text-sm rounded p-3 ' +
                (lastResult.ok
                  ? 'bg-[var(--color-status-completed-bg)] text-[var(--color-status-completed)]'
                  : 'bg-[var(--color-danger-tint)] text-[var(--color-danger)]')
              }>
                {lastResult.ok ? (
                  <>
                    <strong>{lastResult.dryRun ? 'Dry run complete.' : 'Import complete.'}</strong>{' '}
                    {lastResult.successCount} succeeded, {lastResult.errorCount} failed.
                    {lastResult.errors && lastResult.errors.length > 0 && (
                      <ul className="mt-2 text-xs font-mono">
                        {lastResult.errors.slice(0, 10).map((e, i) => (
                          <li key={i}>row {e.row + 1}: {e.error}</li>
                        ))}
                        {lastResult.errors.length > 10 && <li>… and {lastResult.errors.length - 10} more</li>}
                      </ul>
                    )}
                  </>
                ) : (
                  <><strong>Failed.</strong> {lastResult.error}</>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <ImportHistoryList />
    </div>
  );
}
